import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import ProFeatureGate from '@/components/ProFeatureGate';
import { useMonth } from '@/contexts/MonthContext';
import { buildExtendedMonthList } from '@/lib/months';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { fmtMoney } from '@/lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PieChart as PCI, BarChart3, Activity, ChevronLeft, ChevronRight, Calendar, Info } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';

const COLORS = ['hsl(136,18%,35%)', 'hsl(43,74%,50%)', 'hsl(8,52%,51%)', 'hsl(197,37%,44%)', 'hsl(280,45%,55%)', 'hsl(30,70%,50%)', 'hsl(180,40%,40%)', 'hsl(350,60%,50%)'];
const fmt = (n) => fmtMoney(n);
const MN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const getML = (y, m) => `${MN[parseInt(m, 10) - 1]} ${y}`;
const navM = (y, m, d) => { let mi = parseInt(m, 10) + d, yi = parseInt(y, 10); while (mi > 12) { mi -= 12; yi++; } while (mi < 1) { mi += 12; yi--; } return { year: String(yi), month: String(mi).padStart(2, '0') }; };

function BudgetRuleViz({ data }) {
  if (!data?.baseline) return null;
  const buckets = [
    { k: 'needs', l: 'Needs (50%)', t: 50, c: 'hsl(136,18%,35%)', desc: 'Essential expenses: housing, groceries, utilities, insurance, transportation, loan payments', higherIsBad: true },
    { k: 'wants', l: 'Wants (30%)', t: 30, c: 'hsl(43,74%,50%)', desc: 'Discretionary: dining out, entertainment, travel, shopping, subscriptions', higherIsBad: true },
    { k: 'savings', l: 'Savings & Investing (20%)', t: 20, c: 'hsl(197,37%,44%)', desc: 'Future wealth: index funds, emergency savings, retirement, aggressive debt payoff', higherIsBad: false },
  ];
  const pieData = buckets.map(b => ({ name: b.l, value: data[b.k]?.amount || 0 })).filter(d => d.value > 0);

  return (
    <Card className="border border-border shadow-none" data-testid="budget-rule-analytics-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2"><Activity className="h-4 w-4 text-primary" />The 50/30/20 Budget Rule</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            {pieData.length > 0 && (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart><Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" stroke="none" paddingAngle={3}>{pieData.map((_, i) => <Cell key={i} fill={buckets[i]?.c || COLORS[i]} />)}</Pie><Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: '12px', borderRadius: '6px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }} itemStyle={{ color: 'hsl(var(--card-foreground))' }} labelStyle={{ color: 'hsl(var(--card-foreground))' }} /></PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="space-y-4">
            {buckets.map((b) => {
              const d = data[b.k];
              const pct = d?.percentage || 0;
              const isGood = b.higherIsBad ? pct <= b.t + 5 : pct >= b.t - 5;
              return (
                <div key={b.k}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-semibold">{b.l}</span>
                    <span className={`text-sm font-bold tabular-nums ${isGood ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>{pct}%</span>
                  </div>
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(pct * 100 / b.t, 150)}%`, maxWidth: '100%', backgroundColor: isGood ? b.c : 'hsl(8,52%,51%)' }} />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>{fmt(d?.amount || 0)}</span><span>Target: {fmt(d?.target_amount || 0)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{b.desc}</p>
                  {(d?.breakdown || []).length > 0 && (
                    <div className="mt-1.5 space-y-0.5 pl-2 border-l-2 border-border">
                      {d.breakdown.slice(0, 4).map((item) => (
                        <div key={item.category} className="flex justify-between text-xs"><span className="text-muted-foreground">{item.category}</span><span className="tabular-nums">{fmt(item.amount)}</span></div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-4 p-3 bg-muted/50 rounded-md border border-border">
          <div className="flex items-start gap-2"><Info className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" /><p className="text-xs text-muted-foreground italic leading-relaxed">&ldquo;{data.buffett_quote}&rdquo;<span className="font-medium not-italic"> — Warren Buffett</span></p></div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AnalyticsPage() {
  useDisplayCurrency();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [summary, setSummary] = useState(null);
  const [health, setHealth] = useState(null);
  const [budgetRule, setBudgetRule] = useState(null);
  const [debtAnalysis, setDebtAnalysis] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const { year: currentYear, month: currentMonth, setMonth } = useMonth();
  const [loading, setLoading] = useState(true);

  // Only fetch data if the user has Pro access (dashboard routes are unprotected —
  // they're shared with the free Dashboard, so we guard access here in the frontend)
  const hasAccess = !authLoading && user?.has_pro_access === true;

  useEffect(() => {
    if (hasAccess) {
      api.get('/api/dashboard/available-months').then(({ data }) => setAvailableMonths(data || [])).catch(() => {});
    }
  }, [hasAccess]);

  const loadData = useCallback(async (year, month) => {
    if (!hasAccess) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = {}; if (year && month) { params.year = year; params.month = month; }
      const [s, h, br, da] = await Promise.all([
        api.get('/api/dashboard/summary', { params }),
        api.get('/api/dashboard/financial-health', { params }),
        api.get('/api/dashboard/budget-rule', { params }),
        api.get('/api/dashboard/debt-analysis', { params }),
      ]);
      setSummary(s.data); setHealth(h.data); setBudgetRule(br.data); setDebtAnalysis(da.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [hasAccess]);

  useEffect(() => { loadData(currentYear, currentMonth); }, [loadData, currentYear, currentMonth]);

  const handlePrev = () => { if (currentYear && currentMonth) { const n = navM(currentYear, currentMonth, -1); setMonth(n.year, n.month); } };
  const handleNext = () => { if (currentYear && currentMonth) { const n = navM(currentYear, currentMonth, 1); setMonth(n.year, n.month); } };
  const handleCatClick = (cat) => { if (cat && currentYear && currentMonth) navigate(`/transactions?year=${currentYear}&month=${currentMonth}&category=${encodeURIComponent(cat)}`); };

  const extendedMonths = useMemo(() => buildExtendedMonthList(availableMonths, 24, 12), [availableMonths]);

  // Show paywall immediately for non-Pro users (before any data is fetched)
  if (!authLoading && !hasAccess) {
    return (
      <ProFeatureGate feature="Advanced Analytics">
        <div />
      </ProFeatureGate>
    );
  }

  if (loading && !summary) {
    return (
      <ProFeatureGate feature="Advanced Analytics">
        <div className="space-y-6 animate-pulse">
          <div className="h-8 w-48 bg-muted rounded" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-64 bg-muted rounded-md" />)}
          </div>
        </div>
      </ProFeatureGate>
    );
  }

  const s = summary || {};
  const h = health || {};
  const monthLabel = currentYear && currentMonth ? getML(currentYear, currentMonth) : '';
  const catData = Object.entries(s.category_breakdown || {}).sort((a, b) => b[1] - a[1]).map(([name, value]) => ({ name, value: Math.round(value) }));
  const dailyData = (s.daily_expenses || []).map((d) => ({ day: d.day, date: d.date, amount: d.amount }));
  const topMerchants = (s.top_merchants || []).slice(0, 10).map((m) => ({ name: m.merchant?.length > 18 ? m.merchant.slice(0, 18) + '...' : m.merchant, full: m.merchant, amount: Math.round(m.amount) }));

  const pageContent = (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-['Outfit']">Analytics</h1><p className="text-sm text-muted-foreground mt-1">Detailed spending analysis</p></div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={handlePrev} data-testid="analytics-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
          {extendedMonths.length > 0 ? (
            <Select value={currentYear && currentMonth ? `${currentYear}-${currentMonth}` : ''} onValueChange={(v) => { const [y, m] = v.split('-'); setMonth(y, m); }}>
              <SelectTrigger className="w-[170px] h-8 text-sm" data-testid="analytics-month-selector"><Calendar className="h-3.5 w-3.5 mr-1.5" /><SelectValue placeholder={monthLabel} /></SelectTrigger>
              <SelectContent className="max-h-[400px]">
                {extendedMonths.map((em) => (
                  <SelectItem key={em.month} value={em.month}>
                    <span className={em.hasData ? '' : 'text-muted-foreground'}>{em.label}</span>
                    {em.hasData && <span className="text-xs text-muted-foreground ml-1">({em.count})</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : <span className="text-sm font-medium px-3">{monthLabel}</span>}
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleNext} data-testid="analytics-next-month"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* 50/30/20 Budget Rule */}
      <BudgetRuleViz data={budgetRule} />

      {/* Health + Category Pie */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border border-border shadow-none" data-testid="health-details-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Activity className="h-4 w-4 text-primary" />Financial Health &mdash; {monthLabel}</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-4">
              <div className="text-4xl font-bold tracking-tight font-['Outfit'] tabular-nums">{h.score || 0}</div>
              <div><Badge className={`text-xs border-0 ${h.rating === 'Excellent' || h.rating === 'Good' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : h.rating === 'Fair' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'}`}>{h.rating || 'No Data'}</Badge></div>
            </div>
            <div className="space-y-2">
              {(h.details || []).map((d, i) => (
                <div key={i} className="p-2.5 rounded-md border border-border">
                  <div className="flex items-center justify-between mb-0.5"><span className="text-sm font-medium">{d.category}</span><div className={`h-2 w-2 rounded-full ${d.status === 'good' ? 'bg-green-500' : d.status === 'over' ? 'bg-red-500' : 'bg-yellow-500'}`} /></div>
                  <p className="text-xs text-muted-foreground">{d.message}</p>
                  {d.potential_savings > 0 && <p className="text-xs font-medium text-primary mt-0.5">Potential savings: {fmt(d.potential_savings)}/mo</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border shadow-none" data-testid="cat-pie-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><PCI className="h-4 w-4 text-primary" />Spending by Category</CardTitle></CardHeader>
          <CardContent>
            {catData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart><Pie data={catData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={2} stroke="none" className="cursor-pointer" onClick={(_, idx) => handleCatClick(catData[idx]?.name)}>{catData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={(v, n) => [fmt(v), n]} contentStyle={{ fontSize: '12px', borderRadius: '6px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }} itemStyle={{ color: 'hsl(var(--card-foreground))' }} labelStyle={{ color: 'hsl(var(--card-foreground))' }} /></PieChart>
                </ResponsiveContainer>
                <div className="space-y-1 mt-2">{catData.map((d, i) => <div key={d.name} className="flex justify-between text-xs cursor-pointer hover:bg-accent/50 rounded px-1 py-0.5" onClick={() => handleCatClick(d.name)}><div className="flex items-center gap-1.5"><div className="h-2 w-2 rounded-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }} /><span className="text-muted-foreground">{d.name}</span></div><span className="tabular-nums font-medium">{s.income > 0 ? ((d.value / s.income) * 100).toFixed(0) : 0}% &middot; {fmt(d.value)}</span></div>)}</div>
              </>
            ) : <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground">No spending data</div>}
          </CardContent>
        </Card>
      </div>

      {/* Daily + Merchants */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border border-border shadow-none" data-testid="daily-expenses-analytics-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Daily Expenses &mdash; {monthLabel}</CardTitle></CardHeader>
          <CardContent>
            {dailyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={dailyData} className="cursor-pointer" onClick={(d) => { if (d?.activePayload?.[0]?.payload?.date) navigate(`/transactions?year=${currentYear}&month=${currentMonth}&search=${d.activePayload[0].payload.date}`); }}>
                  <defs><linearGradient id="dgA" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(136,18%,35%)" stopOpacity={0.15} /><stop offset="95%" stopColor="hsl(136,18%,35%)" stopOpacity={0} /></linearGradient></defs>
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} interval={2} /><YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                  <Tooltip formatter={(v) => [fmt(v), 'Spent']} labelFormatter={(l, p) => p?.[0]?.payload?.date || `Day ${l}`} contentStyle={{ fontSize: '12px', borderRadius: '6px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }} itemStyle={{ color: 'hsl(var(--card-foreground))' }} labelStyle={{ color: 'hsl(var(--card-foreground))' }} />
                  <Area type="monotone" dataKey="amount" stroke="hsl(136,18%,35%)" strokeWidth={2} fill="url(#dgA)" dot={false} activeDot={{ r: 4, strokeWidth: 0, fill: 'hsl(136,18%,35%)' }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="h-[240px] flex items-center justify-center text-sm text-muted-foreground">No daily data</div>}
          </CardContent>
        </Card>

        <Card className="border border-border shadow-none" data-testid="merchants-bar-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" />Top Merchants</CardTitle></CardHeader>
          <CardContent>
            {topMerchants.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={topMerchants} layout="vertical" margin={{ left: 10 }} className="cursor-pointer" onClick={(d) => { if (d?.activePayload?.[0]?.payload?.full) navigate(`/transactions?year=${currentYear}&month=${currentMonth}&search=${encodeURIComponent(d.activePayload[0].payload.full)}`); }}>
                  <XAxis type="number" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} /><YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} width={110} />
                  <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: '12px', borderRadius: '6px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }} itemStyle={{ color: 'hsl(var(--card-foreground))' }} labelStyle={{ color: 'hsl(var(--card-foreground))' }} />
                  <Bar dataKey="amount" fill="hsl(136,18%,35%)" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-[280px] flex items-center justify-center text-sm text-muted-foreground">No merchant data</div>}
          </CardContent>
        </Card>
      </div>

      {/* Debt Analysis */}
      {debtAnalysis && (
        <div className="grid grid-cols-1 gap-4">
          <Card className="border border-border shadow-none" data-testid="debt-ratio-card">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Total Debt-to-Asset Ratio</CardTitle></CardHeader>
            <CardContent>
              {(() => {
                const r = debtAnalysis.debt_to_asset_ratio;
                const color = r > 150 ? 'text-red-500' : r >= 100 ? 'text-orange-500' : r >= 50 ? 'text-amber-500' : r >= 25 ? 'text-green-600 dark:text-green-400' : 'text-emerald-600 dark:text-emerald-400';
                const barColor = r > 150 ? 'hsl(8,52%,51%)' : r >= 100 ? 'hsl(25,85%,55%)' : r >= 50 ? 'hsl(43,74%,50%)' : r >= 25 ? 'hsl(136,18%,35%)' : 'hsl(152,55%,38%)';
                const badgeClass = r > 150 ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' : r >= 100 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' : r >= 50 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' : r >= 25 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
                const hint = r > 150 ? '>150% needs work' : r >= 100 ? '100–150% is OK' : r >= 50 ? '50–100% is Good' : r >= 25 ? '25–50% is Excellent' : '0–25% is Perfect';
                return (
                  <>
                    <div className="flex items-center gap-4 mb-3">
                      <div className={`text-4xl font-bold tracking-tight font-['Outfit'] tabular-nums ${color}`}>{r}%</div>
                      <div><Badge className={`text-xs border-0 ${badgeClass}`}>{debtAnalysis.debt_risk}</Badge><p className="text-xs text-muted-foreground mt-0.5">{hint}</p></div>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden mb-2">
                      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(r / 200 * 100, 100)}%`, backgroundColor: barColor }} />
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Assets: {fmt(debtAnalysis.total_assets_cad)}</span><span>Liabilities: {fmt(debtAnalysis.total_liabilities_cad)}</span>
                    </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );

  return (
    <ProFeatureGate feature="Advanced Analytics">
      {pageContent}
    </ProFeatureGate>
  );
}
